/**
 * Core chat types and enums
 */

// ========== Chat Type Definitions ==========

export const CHAT_TYPE = {
  CLIENT: 'CLIENT_CHAT',
  ADMIN: 'ADMIN_AI_CHAT',
} as const

export type ChatType = typeof CHAT_TYPE[keyof typeof CHAT_TYPE]

// ========== Owner Type Definitions ==========

export const OWNER_TYPE = {
  CLIENT: 'CLIENT',
  ADMIN: 'ADMIN',
  ASSISTANT: 'ASSISTANT',
} as const

export type OwnerType = typeof OWNER_TYPE[keyof typeof OWNER_TYPE]

// ========== Role Definitions ==========

export const MESSAGE_ROLE = {
  USER: 'user',
  ASSISTANT: 'assistant',
  ERROR: 'error',
  SYSTEM: 'system',
} as const

export type MessageRole = typeof MESSAGE_ROLE[keyof typeof MESSAGE_ROLE]

// ========== Assistant Type Definitions ==========

export const ASSISTANT_TYPE = {
  FAE: 'fae',
  MINGO: 'mingo',
} as const

export type AssistantType = typeof ASSISTANT_TYPE[keyof typeof ASSISTANT_TYPE]

// ========== Author Type Definitions ==========

export const AUTHOR_TYPE = {
  USER: 'user',
  ADMIN: 'admin',
  FAE: 'fae',
  MINGO: 'mingo',
  SYSTEM: 'system',
} as const

export type AuthorType = typeof AUTHOR_TYPE[keyof typeof AUTHOR_TYPE]

// ========== Approval Status Definitions ==========

export const APPROVAL_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled',
} as const

export type ChatApprovalStatus = typeof APPROVAL_STATUS[keyof typeof APPROVAL_STATUS]

// ========== Approval Block Variant Definitions ==========

/**
 * Who is LOOKING at an approval block — not which assistant produced it.
 * `admin` (default) shows the full block: command/script preview, expandable
 * args/result, tool icon. `client` (end-user Fae desktop app) shows only the
 * BE-generated title plus Approve/Reject or the resolved-status pill — raw
 * commands and scripts must never reach the end client. Admin surfaces that
 * render a Fae dialog (tickets dialog client tab) keep the admin variant.
 */
export const APPROVAL_BLOCK_VARIANT = {
  ADMIN: 'admin',
  CLIENT: 'client',
} as const

export type ApprovalBlockVariant = typeof APPROVAL_BLOCK_VARIANT[keyof typeof APPROVAL_BLOCK_VARIANT]

// ========== Connection Status Definitions ==========

export const CONNECTION_STATUS = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting',
  CLOSED: 'closed',
  ERROR: 'error',
} as const

export type ConnectionStatus = typeof CONNECTION_STATUS[keyof typeof CONNECTION_STATUS]

// ========== Message Owner Interface ==========

export interface MessageOwner {
  type: OwnerType
  machineId?: string
  userId?: string
  model?: string
  user?: {
    id: string
    firstName?: string
    lastName?: string
    /** Optional author avatar. Host queries it on the message owner (e.g.
     *  GraphQL `User.image`); `imageUrl` may be relative — the host is
     *  responsible for resolving it to an absolute/proxied URL. */
    image?: { imageUrl?: string | null } | null
  }
}

// ========== Content-shape helpers ==========

import type { MessageContent, MessageSegment } from './message.types'

/** Helper function to check if content is structured (segment array). */
export function isStructuredContent(content: MessageContent): content is MessageSegment[] {
  return Array.isArray(content)
}

/** Normalize either a string or segment array into a uniform segment array. */
export function normalizeContent(content: MessageContent): MessageSegment[] {
  if (typeof content === 'string') {
    return content ? [{ type: 'text', text: content }] : []
  }
  return content
}

// ========== SSE event data shapes (wire shapes) ==========

export interface TextEventData {
  type: 'TEXT'
  text: string
}

export interface ToolExecutionEventData {
  type: 'EXECUTING_TOOL' | 'EXECUTED_TOOL'
  integratedToolType: string
  toolFunction: string
  parameters?: Record<string, any>
  result?: string
  success?: boolean
}

export type SSEEventData = TextEventData | ToolExecutionEventData

// ========== Quick-action chip shape ==========

export interface QuickAction {
  id: string
  text: string
}
